import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { WebsiteFeature, ServicePreset } from './useWebsiteFeatures';
import { useEffect } from 'react';
import { FALLBACK_FEATURES, FALLBACK_PRESETS } from '@/data/packageFallback';

// Cache keys
const PACKAGE_KEYS = {
  features: ['website-features'] as const,
  presets: ['service-presets'] as const,
  all: () => [...PACKAGE_KEYS.features, ...PACKAGE_KEYS.presets] as const,
};

// Fetch functions with proper error handling
const fetchWebsiteFeatures = async (): Promise<WebsiteFeature[]> => {
  console.log('🚀 Fetching website features...');
  
  const { data, error } = await supabase
    .from('website_services')
    .select('*')
    .eq('is_active', true)
    .order('display_order');

  if (error) {
    console.error('❌ Error fetching features:', error);
    throw new Error(`Failed to fetch website features: ${error.message}`);
  }

  console.log('✅ Website features loaded:', (data || []).length);
  return (data || []) as WebsiteFeature[];
};

const fetchServicePresets = async (): Promise<ServicePreset[]> => {
  console.log('🚀 Fetching service presets...');
  
  const { data, error } = await supabase
    .from('service_presets')
    .select('*')
    .eq('is_active', true)
    .order('display_order');

  if (error) {
    console.error('❌ Error fetching presets:', error);
    throw new Error(`Failed to fetch service presets: ${error.message}`);
  }

  // Filter to only show presets marked for landing page
  const landingPresets = (data || []).filter(preset => preset.show_on_landing && preset.is_active);
  console.log('✅ Landing page presets loaded:', landingPresets.length);
  return landingPresets as ServicePreset[];
};

// Custom hooks using React Query
export function useWebsiteFeaturesQuery() {
  const { toast } = useToast();
  
  const query = useQuery<WebsiteFeature[], Error>({
    queryKey: PACKAGE_KEYS.features,
    queryFn: fetchWebsiteFeatures,
    staleTime: 15 * 60 * 1000, // 15 minutes - aggressive caching
    gcTime: 30 * 60 * 1000, // 30 minutes (formerly cacheTime)
    retry: 1, // Single retry for speed
    retryDelay: 500, // Fast retry
  });

  // Handle errors with useEffect
  useEffect(() => {
    if (query.isError) {
      console.error('🔥 Features query failed:', query.error);
      toast({
        title: 'Failed to load website features',
        description: 'Please check your connection and try again.',
        variant: 'destructive',
      });
    }
  }, [query.isError, query.error, toast]);

  return query;
}

export function useServicePresetsQuery() {
  const { toast } = useToast();
  
  const query = useQuery<ServicePreset[], Error>({
    queryKey: PACKAGE_KEYS.presets,
    queryFn: fetchServicePresets,
    staleTime: 15 * 60 * 1000, // 15 minutes - aggressive caching
    gcTime: 30 * 60 * 1000, // 30 minutes (formerly cacheTime)
    retry: 1, // Single retry for speed
    retryDelay: 500, // Fast retry
  });

  // Handle errors with useEffect
  useEffect(() => {
    if (query.isError) {
      console.error('🔥 Presets query failed:', query.error);
      toast({
        title: 'Failed to load service packages',
        description: 'Please check your connection and try again.',
        variant: 'destructive',
      });
    }
  }, [query.isError, query.error, toast]);

  return query;
}

// Combined hook for both features and presets
export function usePackageData() {
  const featuresQuery = useWebsiteFeaturesQuery();
  const presetsQuery = useServicePresetsQuery();

  console.log('📦 usePackageData state:', {
    featuresLoading: featuresQuery.isLoading,
    featuresError: featuresQuery.isError,
    featuresData: featuresQuery.data?.length,
    presetsLoading: presetsQuery.isLoading,
    presetsError: presetsQuery.isError,
    presetsData: presetsQuery.data?.length,
  });

  // Use fallback data if queries fail but are no longer loading
  const features: WebsiteFeature[] = featuresQuery.data ?? 
    (featuresQuery.isError && !featuresQuery.isLoading ? FALLBACK_FEATURES : []);
  const presets: ServicePreset[] = presetsQuery.data ?? 
    (presetsQuery.isError && !presetsQuery.isLoading ? FALLBACK_PRESETS : []);
  
  // Only show loading if queries are actually in loading state AND we don't have cached data
  const isLoading = (featuresQuery.isLoading && !featuresQuery.data) || 
                    (presetsQuery.isLoading && !presetsQuery.data);
  const isError = featuresQuery.isError || presetsQuery.isError;
  const error = featuresQuery.error || presetsQuery.error;
  
  // Show loading only if we truly have no data and queries are loading
  const shouldShowLoading = isLoading;
  
  // Show error only if queries failed and we have no fallback data
  const shouldShowError = isError && !isLoading && features.length === 0 && presets.length === 0;

  // Group features by category for convenience
  const basicFeatures = features.filter(f => f.category === 'basic');
  const advancedFeatures = features.filter(f => f.category === 'advanced');
  const ecommerceFeatures = features.filter(f => f.category === 'ecommerce');
  const businessFeatures = features.filter(f => f.category === 'business');
  const marketingFeatures = features.filter(f => f.category === 'marketing');
  const pageStructureFeatures = features.filter(f => f.category === 'page_structure');

  return {
    // Data
    features,
    presets,
    basicFeatures,
    advancedFeatures,
    ecommerceFeatures,
    businessFeatures,
    marketingFeatures,
    pageStructureFeatures,
    
    // States (with smart fallback logic)
    isLoading: shouldShowLoading,
    isError: shouldShowError,
    error,
    
    // Raw query states for debugging
    _rawLoading: isLoading,
    _rawError: isError,
    
    // Individual query states for more granular control
    featuresQuery,
    presetsQuery,
    
    // Refetch functions
    refetch: () => {
      featuresQuery.refetch();
      presetsQuery.refetch();
    },
  };
}

// Preloader function to start fetching data early
export function preloadPackageData(queryClient: ReturnType<typeof useQueryClient>) {
  console.log('🎯 Preloading package data...');
  
  // Prefetch both queries with aggressive caching
  queryClient.prefetchQuery({
    queryKey: PACKAGE_KEYS.features,
    queryFn: fetchWebsiteFeatures,
    staleTime: 15 * 60 * 1000,
  });
  
  queryClient.prefetchQuery({
    queryKey: PACKAGE_KEYS.presets,
    queryFn: fetchServicePresets,
    staleTime: 15 * 60 * 1000,
  });
}